import { Grid, TextField, Typography } from '@mui/material';
import { Autocomplete } from '@mui/material';
import {
  useFindOneInventoryHardwareQuery,
  useGetAllTypesQuery,
  useRemoveInventoryHardwareMutation,
} from '../../api/api-hooks';
import ItemOptionsBar from '../../components/ItemOptionsBar';
import { truncateISOString } from '../../utils/dateTime';
import { Item } from '../../utils/sharedTypes';
import { nullStringCheck } from '../../utils/stringManipulation';
import AnalyzerTypeManagementButton from '../analyzer-types/AnalyzerTypeManagementButton';
import {
  analyzerValidator,
  inventoryHardwareMiddlware,
} from './inventoryHardwareUtils';
import { AddForm } from './inventoryTypes';
import useInventoryHardwareItemCreator from './useInventoryHardwareItemCreator';
import useInventoryHardwareItemUpdater from './useInventoryHardwareItemUpdater';

interface Props {
  selectedAnalyzers: Item[];
  setSelectedAnalyzers: React.Dispatch<React.SetStateAction<Item[]>>;
  editModalOpenFlag: boolean;
  setEditModalOpenFlag: React.Dispatch<React.SetStateAction<boolean>>;
}

const addFormInitialState: AddForm = {
  serial: '',
  hardwareType: null,
  macAddress: '',
  dateReceived: new Date().toISOString().slice(0, 10),
};

const InventoryHardwareOptionsBar = ({
  selectedAnalyzers,
  setSelectedAnalyzers,
  editModalOpenFlag,
  setEditModalOpenFlag,
}: Props) => {
  const [deleteAnalyzer] = useRemoveInventoryHardwareMutation();
  const analyzerUpdater = useInventoryHardwareItemUpdater();
  const { data: analyzerTypes } = useGetAllTypesQuery({});
  const analyzerCreator = useInventoryHardwareItemCreator();
  if (analyzerTypes === undefined) return null;
  return (
    <>
      <ItemOptionsBar
        selectedItems={selectedAnalyzers}
        setSelectedItems={setSelectedAnalyzers}
        handlerMiddleware={inventoryHardwareMiddlware}
        itemValidator={analyzerValidator}
        formInstructions='Serial and type are required.'
        addFormInitialState={addFormInitialState}
        itemName='Inventory Hardware'
        editModalOpen={editModalOpenFlag}
        setEditModalOpen={setEditModalOpenFlag}
        childrenCreator={(
          addFormState: AddForm,
          handler: any,
          handleEnter: any
        ) => (
          <Grid container spacing={4}>
            <Grid item container xs={6} direction='column' spacing={1}>
              <Grid item>
                <Typography>Serial: </Typography>
              </Grid>
              <Grid item>
                <TextField
                  autoFocus
                  margin='dense'
                  name='serial'
                  fullWidth
                  value={addFormState.serial}
                  onChange={handler}
                  onKeyDown={handleEnter}
                />
              </Grid>
              <Grid item>
                <Autocomplete
                  onChange={(event, value) => {
                    handler(event, value, 'hardwareType');
                  }}
                  value={addFormState.hardwareType}
                  options={analyzerTypes}
                  getOptionLabel={(type) => type.typeName}
                  style={{ width: 300 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Select Type'
                      variant='outlined'
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <AnalyzerTypeManagementButton />
              </Grid>
            </Grid>
            <Grid item container xs={6} direction='column' spacing={1}>
              <Grid item>
                <Typography noWrap>MAC Address:</Typography>
              </Grid>
              <Grid item>
                <TextField
                  name='macAddress'
                  margin='dense'
                  fullWidth
                  value={addFormState.macAddress}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <Typography>Date Received:</Typography>
              </Grid>
              <Grid item>
                <TextField
                  type='date'
                  margin='dense'
                  name='dateReceived'
                  value={truncateISOString(
                    nullStringCheck(addFormState.dateReceived)
                  )}
                  onChange={handler}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
        itemCreator={analyzerCreator}
        itemGetter={useFindOneInventoryHardwareQuery}
        itemUpdater={analyzerUpdater}
        itemDeleter={deleteAnalyzer}
      />
    </>
  );
};

export default InventoryHardwareOptionsBar;
